import { LEGAL_VERSION } from '@falasta/contracts';

/**
 * Scelta dell'utente sui cookie non necessari.
 *
 * Solo due esiti, e nessuno dei due è il default: finché la persona non decide,
 * la risposta non è «granted» né «denied» ma **assente**, e il codice di misura
 * non parte. È la differenza fra chiedere il consenso e prenderselo.
 */
export type ConsentDecision = 'granted' | 'denied';

export interface StoredConsent {
  readonly analytics: ConsentDecision;
  /** Versione dei documenti su cui la scelta è stata espressa. */
  readonly version: string;
  /** ISO-8601: serve a poter dire *quando* è stata data. */
  readonly decidedAt: string;
}

/**
 * Chiave nel `localStorage`, non un cookie.
 *
 * Un cookie di preferenze verrebbe spedito a ogni richiesta senza servire a
 * nessuno lato server, e sarebbe un cookie in più da dichiarare. Il
 * `localStorage` è tecnico per definizione: registra la scelta dell'utente e
 * niente altro.
 */
export const CONSENT_STORAGE_KEY = 'falasta.cookie-consent';

/**
 * La versione a cui una scelta si riferisce.
 *
 * È quella dei documenti legali: se cambia la cookie policy, le scelte
 * precedenti non valgono più e il banner ricompare. Una preferenza non può
 * sopravvivere al testo che descriveva ciò a cui si acconsentiva.
 */
export const CONSENT_VERSION = LEGAL_VERSION;

/** Il minimo indispensabile del `localStorage`: così i test non hanno bisogno di un DOM. */
export interface ConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isDecision(value: unknown): value is ConsentDecision {
  return value === 'granted' || value === 'denied';
}

/**
 * Legge la scelta registrata, o `null` se non c'è, è illeggibile o riguarda una
 * versione superata.
 *
 * Ogni percorso di errore finisce su `null`, cioè su «chiedi di nuovo»: il
 * dubbio si risolve non misurando. Anche un `localStorage` che lancia (Safari in
 * navigazione privata, storage pieno) deve produrre un banner, non una pagina
 * rotta.
 */
export function readConsent(
  storage: ConsentStorage,
  version: string = CONSENT_VERSION,
): StoredConsent | null {
  let raw: string | null;

  try {
    raw = storage.getItem(CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const candidate = parsed as Record<string, unknown>;

  if (!isDecision(candidate.analytics) || candidate.version !== version) {
    return null;
  }

  return {
    analytics: candidate.analytics,
    version,
    decidedAt: typeof candidate.decidedAt === 'string' ? candidate.decidedAt : '',
  };
}

export interface WriteConsentOptions {
  readonly version?: string;
  readonly now?: () => Date;
}

/**
 * Registra la scelta. Se lo storage non è disponibile la scelta vale per la
 * pagina corrente e verrà richiesta di nuovo: meglio richiedere che dedurre.
 */
export function writeConsent(
  storage: ConsentStorage,
  analytics: ConsentDecision,
  options: WriteConsentOptions = {},
): StoredConsent {
  const stored: StoredConsent = {
    analytics,
    version: options.version ?? CONSENT_VERSION,
    decidedAt: (options.now ?? ((): Date => new Date()))().toISOString(),
  };

  try {
    storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Nessun rilancio: la scelta è stata espressa, non poterla ricordare non la
    // annulla.
  }

  return stored;
}

/** Dimentica la scelta: il banner ricompare. Serve a «cambia preferenze». */
export function clearConsent(storage: ConsentStorage): void {
  try {
    storage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Come sopra.
  }
}
